import type { ChangeEventHandler } from 'react'
import { useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { globalTopicsList } from 'recoil/atoms'
import { analyticsTracker } from 'utils'

import { GLOBAL_TOPIC_IDS_KEY, NOT_GLOBAL_TOPIC_IDS_KEY } from './global-topic-filter.consts'
import type { IGlobalTopicFilter } from './global-topic-filter.types'

const { FILTER } = analyticsTracker.eventNames

export const useGlobalTopicFilterModel = (): IGlobalTopicFilter => {
  const [search, setSearch] = useState('')
  const globalTopicsListState = useRecoilValue(globalTopicsList)

  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)
  const globalTopicsInURL = searchParams.getAll(GLOBAL_TOPIC_IDS_KEY)
  const notGlobalTopicsInURL = searchParams.getAll(NOT_GLOBAL_TOPIC_IDS_KEY)

  const territoriesList = useMemo(() => {
    return (globalTopicsListState || [])
      .map(territory => {
        const value = territory?.territoryId?.split('.')?.[1]
        const isInIncluded = globalTopicsInURL.includes(value)
        const isExcluded = notGlobalTopicsInURL.includes(value)
        return {
          ...territory,
          isInIncluded,
          isExcluded,
          value,
        }
      })
      .filter(territory => {
        const name = territory.name.toLowerCase()
        const searchLowerCase = search.toLowerCase()
        return name.includes(searchLowerCase)
      })
  }, [globalTopicsInURL, globalTopicsListState, notGlobalTopicsInURL, search])

  const handleToggleTerritory = (territoryId: string, name: string) => {
    analyticsTracker.eventTrack(FILTER, {
      Filter: 'Global Topic',
      'Filter label': name,
      Page: 'Video Search Page',
      Action: 'Include',
    })
    const newParams = new URLSearchParams(location.search)
    const isIncludedInURL = globalTopicsInURL.includes(territoryId)
    if (isIncludedInURL) {
      newParams.delete(GLOBAL_TOPIC_IDS_KEY, territoryId)
      history.push({ search: newParams.toString() })
      return
    }
    newParams.append(GLOBAL_TOPIC_IDS_KEY, territoryId)
    history.push({ search: newParams.toString() })
  }

  const handleToggleExcludeTerritory = (territoryId: string, name: string) => {
    analyticsTracker.eventTrack(FILTER, {
      Filter: 'Global Topic',
      'Filter label': name,
      Page: 'Video Search Page',
      Action: 'Exclude',
    })
    const newParams = new URLSearchParams(location.search)
    const isIncludedInURL = notGlobalTopicsInURL.includes(territoryId)
    if (isIncludedInURL) {
      newParams.delete(NOT_GLOBAL_TOPIC_IDS_KEY, territoryId)
      history.push({ search: newParams.toString() })
      return
    }
    newParams.delete(GLOBAL_TOPIC_IDS_KEY, territoryId)
    newParams.append(NOT_GLOBAL_TOPIC_IDS_KEY, territoryId)
    history.push({ search: newParams.toString() })
  }

  const handleClear = () => {
    analyticsTracker.eventTrack(FILTER, {
      Filter: 'Global Topic',
      Page: 'Video Search Page',
      Action: 'Clear',
    })
    const newParams = new URLSearchParams(location.search)
    globalTopicsInURL.forEach(id => {
      newParams.delete(GLOBAL_TOPIC_IDS_KEY, id)
    })
    notGlobalTopicsInURL.forEach(id => {
      newParams.delete(NOT_GLOBAL_TOPIC_IDS_KEY, id)
    })
    history.push({ search: newParams.toString() })
  }

  const handleSearch: ChangeEventHandler<HTMLInputElement> & ChangeEventHandler<HTMLTextAreaElement> = event => {
    const value = event.target.value
    setSearch(value)
  }

  return { territoriesList, handleToggleTerritory, handleToggleExcludeTerritory, handleClear, search, handleSearch }
}
